import { z } from 'zod';
import { AnswerSetSchema } from './forms/answers.js';
import { FormDefinitionSchema } from './forms/definition.js';
import { FormKeySchema, plainText } from './forms/primitives.js';
/**
 * The intake **API** contract (M8.2, M8.8, M8.9, `API.md` §2.3/§2.4/§2.8,
 * `DATABASE.md` §7.8, FR-INTAKE-01..07).
 *
 * `./forms` owns the *definition* - what a form is, what an answer may be, which
 * answers are sensitive and which questions are visible. This file owns everything that
 * travels: the builder's drafts, the immutable versions, a submission in flight, the
 * staff summary, the PDF export ticket and the pre-visit readiness indicator.
 *
 * ## Three facts the whole milestone rests on
 *
 * 1. **A submission is pinned to a version, not to a form.** Every shape that carries
 *    answers carries `formVersionId` with them, and the API reads the definition back
 *    from that row rather than from `intake_forms.current_version_id`. Publishing a new
 *    version therefore cannot change what a half-finished patient is being asked, and
 *    cannot change what a staff member reads back six months later.
 * 2. **Sensitive answers never travel by accident.** `answers` on the wire is the union
 *    of both storage columns, but only for a caller the API has already decided may see
 *    them: a patient reading their own submission, or staff holding `intake.read`. The
 *    staff summary marks each item `sensitive` so a UI can style it, and the *audit row
 *    is written by the read itself*, not by the UI.
 * 3. **The client's copy of the rules is a convenience.** `validateAnswers` runs in the
 *    browser to make the form pleasant and in the API to make it true (rules §17.2).
 *    `IntakeValidation` below is what the API sends back when the two disagree.
 */
/* --------------------------------------------------------- forms (admin) ---- */
export const FORM_STATUSES = ['draft', 'published', 'archived'];
export const FormStatusSchema = z.enum(FORM_STATUSES);
/**
 * One row of `GET /admin/intake-forms`.
 *
 * `draftDirty` is computed from a hash of the draft against the published version's
 * hash rather than from a timestamp: a builder that saves an unchanged draft every
 * thirty seconds would otherwise show a permanent "unpublished changes" badge, which is
 * the fastest way to teach staff to ignore it.
 */
export const FormSummarySchema = z
    .object({
    id: z.uuid(),
    /** `intake_forms.key`, stable across versions. */
    key: FormKeySchema,
    title: z.string().max(300),
    /** The version patients are being shown, or `null` while the form has never shipped. */
    publishedVersion: z.number().int().min(1).nullable(),
    /** The number the draft will take when it is published. */
    draftVersion: z.number().int().min(1),
    draftDirty: z.boolean(),
    status: FormStatusSchema,
    updatedAt: z.iso.datetime({ offset: true }),
})
    .strict();
/**
 * One published, immutable version.
 *
 * `hash` is the SHA-256 of the canonical JSON of the definition. It is what `draftDirty`
 * compares against, what the PDF footer prints and what makes "is this the form the
 * patient actually saw?" answerable without diffing two blobs of JSON.
 */
export const FormVersionSummarySchema = z
    .object({
    id: z.uuid(),
    version: z.number().int().min(1),
    publishedAt: z.iso.datetime({ offset: true }),
    publishedBy: z.string().max(200),
    /** The changelog note captured in the publish dialog (`{ changelog }` in API.md §2.8). */
    notes: z.string().max(2000),
    hash: z.string().regex(/^[0-9a-f]{64}$/),
})
    .strict();
/** `GET /admin/intake-forms/:id`. */
export const FormDetailSchema = z
    .object({
    form: FormSummarySchema,
    /** `intake_forms.draft_schema` - may be invalid while it is being edited. */
    draftSchema: FormDefinitionSchema,
    /** Newest first. */
    versions: z.array(FormVersionSummarySchema),
})
    .strict();
export const CreateFormRequestSchema = z
    .object({ key: FormKeySchema, title: plainText(300) })
    .strict();
/** `PUT /admin/intake-forms/:id/draft`. The whole definition, validated server-side. */
export const SaveFormDraftRequestSchema = z.object({ schema: FormDefinitionSchema }).strict();
/**
 * `POST /admin/intake-forms/:id/publish`.
 *
 * `notes` rather than API.md §2.8's `changelog`: the column is `changelog`, the dialog
 * calls it notes, and the admin client (`apps/admin/src/lib/api/forms.ts`) was written
 * against `notes` months before the endpoint existed. The wire name follows the client;
 * the column keeps its name.
 */
export const PublishFormRequestSchema = z
    .object({ notes: z.string().trim().max(2000).default('') })
    .strict();
export const FormVersionDetailSchema = z
    .object({ version: FormVersionSummarySchema, schema: FormDefinitionSchema })
    .strict();
export const FormListResponseSchema = z.object({ data: z.array(FormSummarySchema) });
export const FormResponseSchema = z.object({ data: FormSummarySchema });
export const FormDetailResponseSchema = z.object({ data: FormDetailSchema });
export const FormVersionResponseSchema = z.object({ data: FormVersionSummarySchema });
export const FormVersionDetailResponseSchema = z.object({ data: FormVersionDetailSchema });
/* ------------------------------------------------------ the published form ---- */
/**
 * `GET /public/intake/form` and `GET /public/intake-forms/:formId`.
 *
 * What an anonymous visitor is handed: the definition and the id of the version it came
 * from. The form id is *not* the version id and the two are both returned, because the
 * next call ("save my answers against this") has to name the version rather than the
 * form - a publish between page load and first keystroke must not silently move the
 * patient onto different questions.
 */
export const PublishedFormSchema = z
    .object({
    formId: z.uuid(),
    versionId: z.uuid(),
    version: z.number().int().min(1),
    key: FormKeySchema,
    title: z.string().max(300),
    definition: FormDefinitionSchema,
})
    .strict();
export const PublishedFormResponseSchema = z.object({
    data: z.object({ form: PublishedFormSchema }),
});
/* ------------------------------------------------------------- submissions ---- */
/** `intake_submissions.status` (`DATABASE.md` §7.8). */
export const SUBMISSION_STATUSES = ['assigned', 'in_progress', 'submitted', 'reviewed'];
export const SubmissionStatusSchema = z.enum(SUBMISSION_STATUSES);
/**
 * Per-field messages, keyed by field id, in the wording `validateAnswers` produced.
 *
 * A `complete` that fails answers `422 INTAKE_INCOMPLETE` with these in
 * `details.errors`, so the renderer can put each sentence under the field that earned
 * it without re-running its own rules and possibly disagreeing.
 */
export const IntakeValidationSchema = z
    .object({
    valid: z.boolean(),
    errors: z.record(z.string().max(64), z.string().max(300)),
})
    .strict();
/**
 * A submission as the patient's own surfaces see it: the pinned definition and every
 * answer, sensitive ones included, because they are the patient's own.
 *
 * Field names follow `apps/patient/src/portal/types.ts` `IntakePacket`, which the portal
 * was built against. `submissionId`, `formVersionId` and `draftId` are additive: the
 * portal ignores what it does not use and the funnel needs them.
 */
export const IntakePacketSchema = z
    .object({
    submissionId: z.uuid(),
    /** `null` on the public funnel, where the submission hangs off a draft. */
    appointmentId: z.uuid().nullable(),
    /** `null` in the portal, where the submission hangs off an appointment. */
    draftId: z.uuid().nullable(),
    status: SubmissionStatusSchema,
    submittedAt: z.iso.datetime({ offset: true }).nullable(),
    updatedAt: z.iso.datetime({ offset: true }),
    formId: z.uuid(),
    formVersionId: z.uuid(),
    formTitle: z.string().max(300),
    /** The pinned version. A submission is always read back against the version it used. */
    formVersion: z.number().int().min(1),
    definition: FormDefinitionSchema,
    answers: AnswerSetSchema,
    validation: IntakeValidationSchema,
})
    .strict();
export const IntakePacketResponseSchema = z.object({ data: IntakePacketSchema });
/**
 * `POST /public/intake/submissions` and `POST /patient/appointments/:id/intake`.
 *
 * Neither surface names a patient: the funnel's submission belongs to the draft the
 * cookie owns, and the portal's to the session's appointment. `formVersionId` may be
 * omitted, in which case the form's current published version is pinned - which is the
 * right default exactly once, at the moment the submission is created.
 */
export const CreateSubmissionRequestSchema = z
    .object({
    /** Which form, when the tenant has more than one. Defaults to the visit type's. */
    formKey: FormKeySchema.optional(),
    formVersionId: z.uuid().optional(),
    appointmentId: z.uuid().optional(),
})
    .strict();
/**
 * Autosave. A **partial** set of answers, merged into what is stored rather than
 * replacing it, so a renderer may send one field at a time (FR-INTAKE-03, ADR-018).
 *
 * Merged, but not blindly: every answer in the patch is validated against the pinned
 * version, unknown field ids are dropped, and answers to fields the patient's own
 * branch has hidden are dropped with them. A patch whose *present* answers are invalid
 * is `400 VALIDATION_FAILED` - "save as you go" does not mean "store anything".
 */
export const SaveIntakeAnswersRequestSchema = z
    .object({
    /**
     * A **partial** answer set, merged into what is stored.
     *
     * Defaulted to `{}` so "finish what you already saved" is an empty body rather than
     * a body that repeats every answer back at the server. `POST
     * /public/intake/current/complete` is the case that needs it - the funnel autosaved
     * as the patient typed, so by the time they press the button there is nothing left
     * to send - and the portal's `PUT` accepts the same.
     */
    answers: AnswerSetSchema.default({}),
    /**
     * `false` (the default) autosaves; `true` runs the full validation and moves the
     * submission to `submitted`. The portal's `PUT` carries it (API.md §2.4).
     */
    submit: z.boolean().default(false),
})
    .strict();
/* ---------------------------------------------------------- staff summary ---- */
/**
 * One answered question, rendered for a human.
 *
 * `display` is the answer as text - option *labels* rather than option values, "Yes"
 * rather than `true`, a filename rather than a file id - because the staff summary and
 * the PDF are read by a clinician, not by a program. `value` is kept beside it for the
 * screens that do want the raw answer, and both are absent when the caller may not
 * decrypt (`sensitive: true`, `redacted: true`).
 */
export const IntakeSummaryItemSchema = z
    .object({
    fieldId: z.string().max(64),
    label: z.string().max(300),
    type: z.string().max(40),
    sensitive: z.boolean(),
    /** True when this actor may not decrypt: `value` and `display` are then null. */
    redacted: z.boolean(),
    display: z.string().max(20_000).nullable(),
    answered: z.boolean(),
})
    .strict();
export const IntakeSummarySectionSchema = z
    .object({
    id: z.string().max(64),
    title: z.string().max(300),
    items: z.array(IntakeSummaryItemSchema),
})
    .strict();
/**
 * `GET /admin/appointments/:id/intake` and `GET /admin/intake-submissions/:id`.
 *
 * Only the sections and fields the patient actually *saw* appear: a summary that listed
 * every question in the definition would bury the eight answers a clinician needs under
 * ninety "not applicable" rows, and would misrepresent the form the patient filled in.
 */
export const IntakeSummarySchema = z
    .object({
    submissionId: z.uuid(),
    appointmentId: z.uuid().nullable(),
    patientId: z.uuid(),
    status: SubmissionStatusSchema,
    submittedAt: z.iso.datetime({ offset: true }).nullable(),
    reviewedAt: z.iso.datetime({ offset: true }).nullable(),
    reviewedBy: z.string().max(200).nullable(),
    form: z
        .object({
        id: z.uuid(),
        key: FormKeySchema,
        title: z.string().max(300),
        versionId: z.uuid(),
        version: z.number().int().min(1),
    })
        .strict(),
    /** True when this actor holds `intake.read` and the sensitive half was decrypted. */
    includesSensitive: z.boolean(),
    sections: z.array(IntakeSummarySectionSchema),
    generatedAt: z.iso.datetime({ offset: true }),
})
    .strict();
export const IntakeSummaryResponseSchema = z.object({ data: IntakeSummarySchema });
/**
 * `POST /admin/appointments/:id/intake/export`.
 *
 * The PDF is a `files` row of kind `export`, so it is fetched the same way every other
 * object is - a signed GET with a five-minute life - rather than streamed inline. That
 * puts one authorization path in front of every byte of PHI this platform hands out,
 * and it means the export a staff member generated at 09:00 is still auditable at 17:00.
 */
export const IntakeExportSchema = z
    .object({
    fileId: z.uuid(),
    url: z.string().max(4096),
    expiresAt: z.iso.datetime({ offset: true }),
    formVersionId: z.uuid(),
    formVersion: z.number().int().min(1),
    generatedAt: z.iso.datetime({ offset: true }),
})
    .strict();
export const IntakeExportResponseSchema = z.object({ data: IntakeExportSchema });
/* -------------------------------------------------------------- readiness ---- */
/**
 * One leg of the pre-visit readiness indicator (FR-INTAKE-07, M8.9).
 *
 * `missing` and `pending` are different problems: `pending` is waiting on the patient or
 * a payer, `missing` is nothing started. Staff triage them differently, so the indicator
 * must too - this is the vocabulary `apps/admin/src/features/appointments/types.ts` was
 * built against and it is kept exactly.
 */
export const READINESS_STATES = ['ready', 'pending', 'missing'];
export const ReadinessStateSchema = z.enum(READINESS_STATES);
export const INTAKE_READINESS = ['not_started', 'in_progress', 'complete'];
export const IntakeReadinessSchema = z.enum(INTAKE_READINESS);
export const INSURANCE_READINESS = [
    'none',
    'self_pay',
    'verified',
    'pending_review',
    'failed',
];
export const InsuranceReadinessSchema = z.enum(INSURANCE_READINESS);
export const CARD_READINESS = ['on_file', 'missing'];
export const CardReadinessSchema = z.enum(CARD_READINESS);
/**
 * The indicator, in two resolutions at once.
 *
 * The three top-level fields are the traffic light the list and the detail header
 * render; `detail` is the sentence underneath. They are both here rather than only the
 * second because the two audiences are genuinely different: a staff member scanning
 * forty rows wants "2 of 3", and a staff member who has stopped on one row wants to
 * know whether the insurance leg is amber because a payer has not answered or because
 * the check failed. Deriving the light from the detail in every client would be the
 * same rule written four times.
 */
export const ReadinessSchema = z
    .object({
    /** The intake submission reached `submitted` or `reviewed`. */
    intake: ReadinessStateSchema,
    /** An eligibility check verified, or the patient is on the self-pay path. */
    insurance: ReadinessStateSchema,
    /** A `payment_methods` row is `active` for this patient. */
    card: ReadinessStateSchema,
    detail: z
        .object({
        intake: IntakeReadinessSchema,
        insurance: InsuranceReadinessSchema,
        card: CardReadinessSchema,
    })
        .strict(),
    /** All three legs `ready`. Saves every client the same three-way `&&`. */
    ready: z.boolean(),
})
    .strict();
/** How many of the three legs are done. The "2 of 3" summary, defined once. */
export function readyCount(readiness) {
    return [readiness.intake, readiness.insurance, readiness.card].filter((state) => state === 'ready').length;
}
//# sourceMappingURL=intake.js.map